import { apiService } from "./api";

export interface StockInfo {
    stock_id: string;
    product_id: string;
    quantity: number;
    reserved_quantity: number;
    min_stock_level: number;
    max_stock_level: number;
    available_quantity?: number;
    is_low_stock?: boolean;
}

export interface StockAvailability {
    product_id: string;
    available: boolean;
    available_quantity: number;
    required_quantity: number;
    current_stock: number;
    reserved_stock: number;
    reason?: string;
}

export interface StockMovement {
    movement_id: string;
    stock_id: string;
    movement_type: string;
    quantity: number;
    reference_type?: string;
    reference_id?: string;
    notes?: string;
    create_time: string;
}

export interface LowStockItem {
    product_id: string;
    product_name: string;
    sku: string;
    current_quantity: number;
    reserved_quantity: number;
    available_quantity: number;
    min_stock_level: number;
    max_stock_level: number;
    is_low_stock: boolean;
}

export interface StockReservation {
    order_id: string;
    order_number: string;
    status: string;
    order_date: string;
    customer_name?: string;
    quantity: number;
}

export class StockService {
    /**
     * Get stock information for a specific product
     */
    async getProductStock(productId: string): Promise<StockInfo> {
        return apiService.get<StockInfo>(`/stock/${productId}`);
    }

    /**
     * Check stock availability for a specific product
     */
    async checkStockAvailability(
        productId: string,
        quantity: number
    ): Promise<StockAvailability> {
        return apiService.get<StockAvailability>(
            `/stock/availability/${productId}?quantity=${quantity}`
        );
    }

    /**
     * Check stock availability for multiple products
     */
    async checkMultipleStockAvailability(
        items: Array<{ product_id: string; quantity: number }>
    ): Promise<{
        all_available: boolean;
        items: Array<StockAvailability>;
    }> {
        return apiService.post(`/stock/availability/check-multiple`, items);
    }

    /**
     * Add stock for a product
     */
    async addStock(
        productId: string,
        quantity: number,
        referenceType: string = "MANUAL",
        notes?: string
    ): Promise<StockInfo> {
        const params = new URLSearchParams();
        params.append("quantity", quantity.toString());
        params.append("reference_type", referenceType);
        if (notes) params.append("notes", notes);

        return apiService.post<StockInfo>(
            `/stock/${productId}/add?${params.toString()}`
        );
    }

    /**
     * Adjust stock quantity manually
     */
    async adjustStock(
        productId: string,
        newQuantity: number,
        reason?: string
    ): Promise<StockInfo> {
        return apiService.post<StockInfo>(`/stock/${productId}/adjust`, {
            new_quantity: newQuantity,
            reason: reason || "Manual adjustment",
        });
    }

    /**
     * Reserve stock for a product
     */
    async reserveStock(
        productId: string,
        quantity: number,
        referenceId?: string
    ): Promise<{ success: boolean; message: string }> {
        const params = new URLSearchParams();
        params.append("quantity", quantity.toString());
        if (referenceId) params.append("reference_id", referenceId);

        return apiService.post(
            `/stock/${productId}/reserve?${params.toString()}`
        );
    }

    /**
     * Release reserved stock for a product
     */
    async releaseStock(
        productId: string,
        quantity: number,
        referenceId?: string
    ): Promise<{ success: boolean; message: string }> {
        const params = new URLSearchParams();
        params.append("quantity", quantity.toString());
        if (referenceId) params.append("reference_id", referenceId);

        return apiService.post(
            `/stock/${productId}/release?${params.toString()}`
        );
    }

    /**
     * Get stock movements for a product
     */
    async getStockMovements(
        productId: string,
        limit: number = 100
    ): Promise<StockMovement[]> {
        return apiService.get<StockMovement[]>(
            `/stock/movements/${productId}?limit=${limit}`
        );
    }

    /**
     * Get low stock report
     */
    async getLowStockReport(): Promise<LowStockItem[]> {
        return apiService.get<LowStockItem[]>("/stock/reports/low-stock");
    }

    /**
     * Get the orders currently holding a stock reservation for a product
     */
    async getStockReservations(productId: string): Promise<StockReservation[]> {
        return apiService.get<StockReservation[]>(
            `/stock/reservations/${productId}`
        );
    }
}

export const stockService = new StockService();
